import React from 'react'

import { PaginatedResponse } from '@/core/api/dtos/common/types'
import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { Group } from '@/core/api/entities/group'
import { Location } from '@/core/api/entities/location'
import { Zone } from '@/core/api/entities/zone/zone'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import GroupTab from '@/features/zones/List/Tabs/GroupTab'
import { LocationTab } from '@/features/zones/List/Tabs/LocationTab'
import { ZonesTab } from '@/features/zones/List/Tabs/ZonesTab'
import { StatusFilter } from '@/features/zones/List/ZoneStatusFilter'
import PageLayout from '@/shared/components/organisms/Layout/PageLayout'

interface PaginationConfig {
	current: number
	pageSize: number
	onChange: (page: number) => void
	onShowSizeChange: (current: number, size: number) => void
	showQuickJumper: boolean
	showSizeChanger: boolean
}

interface ZonesTabProps {
	zones: PaginatedResponse<Zone> | undefined
	isLoading: boolean
	error: Error | null
	searchTerm: string
	statusFilter: StatusFilter
	setSearchTerm: (value: string) => void
	setStatusFilter: (value: StatusFilter) => void
	handleExportZones: () => void
	openDeleteModal: (zone: Zone) => void
	openRefreshModal: (zone: Zone) => void
	openEditModal: (zone: Zone) => void
	openSendCodeEmailModal: (zone: Zone) => void
	handleAddZone: () => void
	handleConnectZone: (zone: Zone) => void
	handleUpdateZone: (zoneId: number) => (data: UpdateZoneRequestDTO) => void
	isUpdating: boolean
	isTrialAccount?: boolean
	pagination: PaginationConfig
	accountFilter: number | null
	setAccountFilter: (id: number | null) => void
	showAccountFilter: boolean
}

interface LocationsTabProps {
	locations: PaginatedResponse<Location> | undefined
	isLoading: boolean
	error: Error | null
	searchTerm: string
	setSearchTerm: (value: string) => void
	pagination: PaginationConfig
	refetch: () => void
	accountFilter: number | null
	setAccountFilter: (id: number | null) => void
	showAccountFilter: boolean
}

interface GroupsTabProps {
	groups: PaginatedResponse<Group> | undefined
	isLoading: boolean
	error: Error | null
	searchTerm: string
	setSearchTerm: (value: string) => void
	pagination: PaginationConfig
	refetch: () => void
	accountFilter: number | null
	setAccountFilter: (id: number | null) => void
	showAccountFilter: boolean
}

interface ZonesUIProps {
	activeTab: string
	handleTabChange: (tabKey: string) => void
	zonesTab: ZonesTabProps
	locationsTab: LocationsTabProps
	groupsTab: GroupsTabProps
}

const ALL_TABS_ITEMS = [
	{ key: 'zones', label: 'entities.zone.main_tabs.zones' },
	{ key: 'group', label: 'entities.zone.main_tabs.zone_management' },
	{ key: 'locations', label: 'entities.zone.main_tabs.locations' },
]

export const ZonesUI: React.FC<ZonesUIProps> = ({
	activeTab,
	handleTabChange,
	zonesTab,
	locationsTab,
	groupsTab,
}) => {
	const { t } = useTranslation('common')
	const { features } = usePermission()

	const tabs = ALL_TABS_ITEMS.filter(
		(tab) => tab.key !== 'group' || features.group.enabled
	).map((tab) => ({
		key: tab.key,
		label: t(tab.label),
	}))

	return (
		<PageLayout
			tabs={tabs}
			activeTab={activeTab}
			onTabChange={handleTabChange}
			contentClassName="px-4 py-4 lg:px-8 lg:py-6 md:bg-card space-y-6 mb-16"
			topAppBarLeftContent={
				<h1 className="md:hidden text-2xl font-semibold tracking-tight">
					{t('navigation.zones')}
				</h1>
			}
		>
			{activeTab === 'zones' && (
				<ZonesTab
					zones={zonesTab.zones}
					isLoading={zonesTab.isLoading}
					error={zonesTab.error}
					searchTerm={zonesTab.searchTerm}
					statusFilter={zonesTab.statusFilter}
					setSearchTerm={zonesTab.setSearchTerm}
					setStatusFilter={zonesTab.setStatusFilter}
					handleExportZones={zonesTab.handleExportZones}
					openDeleteModal={zonesTab.openDeleteModal}
					openRefreshModal={zonesTab.openRefreshModal}
					openEditModal={zonesTab.openEditModal}
					openSendCodeEmailModal={zonesTab.openSendCodeEmailModal}
					handleAddZone={zonesTab.handleAddZone}
					handleConnectZone={zonesTab.handleConnectZone}
					handleUpdateZone={zonesTab.handleUpdateZone}
					isUpdating={zonesTab.isUpdating}
					isTrialAccount={zonesTab.isTrialAccount}
					pagination={zonesTab.pagination}
					accountFilter={zonesTab.accountFilter}
					setAccountFilter={zonesTab.setAccountFilter}
					showAccountFilter={zonesTab.showAccountFilter}
				/>
			)}

			{activeTab === 'group' && (
				<GroupTab
					groups={groupsTab.groups}
					isLoading={groupsTab.isLoading}
					error={groupsTab.error}
					searchTerm={groupsTab.searchTerm}
					setSearchTerm={groupsTab.setSearchTerm}
					pagination={groupsTab.pagination}
					refetch={groupsTab.refetch}
					accountFilter={groupsTab.accountFilter}
					setAccountFilter={groupsTab.setAccountFilter}
					showAccountFilter={groupsTab.showAccountFilter}
				/>
			)}

			{activeTab === 'locations' && (
				<LocationTab
					locations={locationsTab.locations}
					isLoading={locationsTab.isLoading}
					error={locationsTab.error}
					searchTerm={locationsTab.searchTerm}
					setSearchTerm={locationsTab.setSearchTerm}
					pagination={locationsTab.pagination}
					refetch={locationsTab.refetch}
					accountFilter={locationsTab.accountFilter}
					setAccountFilter={locationsTab.setAccountFilter}
					showAccountFilter={locationsTab.showAccountFilter}
				/>
			)}
		</PageLayout>
	)
}
